import React, { useEffect, useRef, useState } from 'react';
import './PortraitChat.css';

function Icon({ name }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'attach') return <svg {...common}><path d="m20.5 11.5-8.6 8.6a5 5 0 0 1-7.1-7.1l9.2-9.2a3.5 3.5 0 0 1 5 5l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" /></svg>;
  if (name === 'plus') return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
  if (name === 'mic') return <svg {...common}><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" /></svg>;
  if (name === 'send') return <svg {...common}><path d="M12 19V5m-6 6 6-6 6 6" /></svg>;
  return <svg {...common}><path d="M18 6 6 18M6 6l12 12" /></svg>;
}

export default function PortraitChat({ image, name }) {
  const [active, setActive] = useState(false);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [listening, setListening] = useState(false);
  const fileInput = useRef(null);
  const composerInput = useRef(null);
  const recognition = useRef(null);
  const imageSrc = image || '/形象照1.png';
  const voiceSupported = typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => () => recognition.current?.abort(), []);

  useEffect(() => {
    if (active) composerInput.current?.focus();
  }, [active]);

  useEffect(() => {
    if (!messages.length) return;
    document.querySelectorAll('.portrait-chat-conversation').forEach((container) => {
      container.scrollTop = container.scrollHeight;
    });
  }, [messages]);

  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length) {
      setFiles((current) => [...current, ...selected]);
      setActive(true);
    }
    event.target.value = '';
  };

  const send = (event) => {
    event.preventDefault();
    if (!draft.trim() && files.length === 0) return;
    setMessages((current) => [...current, { text: draft.trim(), files: files.map((file) => file.name) }]);
    setDraft('');
    setFiles([]);
  };

  const openFiles = () => fileInput.current?.click();

  const toggleDictation = () => {
    if (listening) {
      recognition.current?.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const nextRecognition = new SpeechRecognition();
    nextRecognition.lang = 'zh-CN';
    nextRecognition.interimResults = false;
    nextRecognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript;
      if (transcript) setDraft((current) => [current, transcript].filter(Boolean).join(' '));
    };
    nextRecognition.onend = () => setListening(false);
    nextRecognition.onerror = () => setListening(false);
    recognition.current = nextRecognition;
    try {
      nextRecognition.start();
      setListening(true);
    } catch (error) {
      setListening(false);
    }
  };

  const composer = () => (
    <form className="portrait-chat-composer" onSubmit={send}>
      <button className="portrait-chat-add" type="button" onClick={openFiles} aria-label="添加附件" title="添加附件">
        <Icon name="plus" />
      </button>
      <div className="portrait-chat-input-shell">
        <input
          ref={composerInput}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="发消息…"
          aria-label="输入消息"
        />
        {draft.trim() || files.length > 0 ? (
          <button className="portrait-chat-send" type="submit" aria-label="加入本地对话" title="发送"><Icon name="send" /></button>
        ) : (
          <button className={`portrait-chat-mic ${listening ? 'portrait-chat-listening' : ''}`} type="button" onClick={toggleDictation} disabled={!voiceSupported} aria-label={voiceSupported ? (listening ? '停止语音输入' : '语音输入') : '浏览器不支持语音输入'} title={voiceSupported ? (listening ? '停止语音输入' : '语音输入') : '浏览器不支持语音输入'}><Icon name="mic" /></button>
        )}
      </div>
    </form>
  );

  const conversation = () => (
    <div className="portrait-chat-conversation">
      {messages.map((message, index) => (
        <div className="portrait-chat-message" key={index}>
          {message.text && <p>{message.text}</p>}
          {message.files.map((file, fileIndex) => <span className="portrait-chat-file" key={`${file}-${fileIndex}`}><Icon name="attach" />{file}</span>)}
        </div>
      ))}
    </div>
  );

  const pendingFiles = files.length > 0 && (
    <div className="portrait-chat-pending" aria-label="待加入的附件">
      {files.map((file, index) => (
        <button type="button" key={`${file.name}-${index}`} onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} title="移除附件">
          {file.name}<Icon name="close" />
        </button>
      ))}
    </div>
  );

  return (
    <>
      <input ref={fileInput} className="portrait-chat-file-input" type="file" multiple onChange={addFiles} aria-label="选择附件" />
      <div className={`portrait-chat ${active ? 'portrait-chat-active' : ''}`}>
        <div className="portrait-chat-photo">
          <img src={imageSrc} alt={name || '个人头像'} onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = '/形象照1.png';
          }} />
        </div>

        {!active && (
          <div className="portrait-chat-teaser">
            <button type="button" className="portrait-chat-add" onClick={openFiles} aria-label="添加附件" title="添加附件">
              <Icon name="plus" />
            </button>
            <button type="button" className="portrait-chat-teaser-input" onClick={() => setActive(true)} aria-label="打开对话框">
              <span>发消息…</span><Icon name="mic" />
            </button>
          </div>
        )}

        {active && (
          <div className="portrait-chat-card">
            <div className="portrait-chat-actions">
              <button type="button" onClick={() => setActive(false)} aria-label="收起对话框" title="收起对话框"><Icon name="close" /></button>
            </div>
            <div className="portrait-chat-heading">
              <strong>{name || '和我聊聊'}</strong>
            </div>
            {conversation()}
            {pendingFiles}
            {composer()}
          </div>
        )}
      </div>

    </>
  );
}
